// src/GDB/components/JiraDatasetForm.jsx
/**
 * JIRA Dataset Form Component
 * 
 * Features:
 * 1. Enter JQL query (filter)
 * 2. Select aggregation type (count, sum, avg, raw, etc.)
 * 3. Configure aggregation parameters
 * 4. Preview results
 * 5. Save/Update/Delete dataset
 * 6. Properly loads existing dataset data when editing
 * 
 * FIXED: Added date fields (created, updated, etc.) to Group By options
 */

import { useState, useEffect, useMemo } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../api/client";

const AGGREGATION_TYPES = [
  {
    value: "count",
    label: "Count",
    description: "Count issues (for KPI or grouped charts)",
    hasGroupBy: true,
    hasField: false,
    outputFields: (config) => config.group_by 
      ? [config.group_by, "count"] 
      : ["count"],
    widgetHint: (config) => config.group_by 
      ? `X: ${config.group_by}, Y: count` 
      : "Value: count",
  },
  {
    value: "sum",
    label: "Sum",
    description: "Sum a numeric field (e.g., story points)",
    hasGroupBy: true,
    hasField: true,
    fieldLabel: "Field to Sum",
    fieldRequired: true,
    outputFields: (config) => config.group_by 
      ? [config.group_by, "sum"] 
      : ["sum"],
    widgetHint: (config) => config.group_by 
      ? `X: ${config.group_by}, Y: sum` 
      : "Value: sum",
  },
  {
    value: "avg",
    label: "Average",
    description: "Average of a numeric field",
    hasGroupBy: true,
    hasField: true,
    fieldLabel: "Field to Average",
    fieldRequired: true,
    outputFields: (config) => config.group_by 
      ? [config.group_by, "avg"] 
      : ["avg"],
    widgetHint: (config) => config.group_by 
      ? `X: ${config.group_by}, Y: avg` 
      : "Value: avg",
  },
  {
    value: "time_series",
    label: "Time Series",
    description: "Track values over time (for line charts)",
    hasDateField: true,
    hasInterval: true,
    hasField: true,
    fieldLabel: "Value Field (optional, for sum)",
    outputFields: () => ["date", "value"],
    widgetHint: () => "X: date, Y: value",
  },
  {
    value: "pivot",
    label: "Pivot Table",
    description: "Cross-tabulation of two fields",
    hasRowField: true,
    hasColumnField: true,
    outputFields: (config) => [config.row_field || "row", "...column values...", "total"],
    widgetHint: () => "Use as Table widget",
  },
  {
    value: "top_n",
    label: "Top N",
    description: "Top N values by count or sum",
    hasGroupBy: true,
    hasN: true,
    hasField: true,
    fieldLabel: "Value Field (optional)",
    outputFields: (config) => [config.group_by || "field", "value"],
    widgetHint: (config) => `X: ${config.group_by || "field"}, Y: value`,
  },
  {
    value: "raw",
    label: "Raw Table",
    description: "Raw issue data as table",
    hasFields: true,
    outputFields: (config) => config.fields || ["key", "summary", "status", "assignee"],
    widgetHint: () => "Use as Table widget",
  },
];

// FIXED: Added date fields to COMMON_FIELDS for Group By dropdown
const COMMON_FIELDS = [
  // Categorical fields
  { value: "status", label: "Status" },
  { value: "assignee", label: "Assignee" },
  { value: "reporter", label: "Reporter" },
  { value: "priority", label: "Priority" },
  { value: "issuetype", label: "Issue Type" },
  { value: "project", label: "Project" },
  { value: "resolution", label: "Resolution" },
  { value: "sprint", label: "Sprint" },
  { value: "labels", label: "Labels" },
  { value: "components", label: "Components" },
  { value: "fixVersions", label: "Fix Versions" },
  { value: "severity", label: "Severity" },
  // FIXED: Added date fields for defect trend analysis
  { value: "created", label: "Created Date" },
  { value: "updated", label: "Updated Date" },
  { value: "resolutiondate", label: "Resolution Date" },
  { value: "duedate", label: "Due Date" },
  // NEW: Calculated fields for aging analysis
  { value: "age_bucket", label: "Age Bucket (0-7, 8-14, 15-30, 30+ days)" },
];

const NUMERIC_FIELDS = [
  { value: "storypoints", label: "Story Points" },
];

const DATE_FIELDS = [
  { value: "created", label: "Created Date" },
  { value: "updated", label: "Updated Date" },
  { value: "resolutiondate", label: "Resolution Date" },
  { value: "duedate", label: "Due Date" },
];

const TABLE_FIELDS = [
  { value: "key", label: "Issue Key" },
  { value: "summary", label: "Summary" },
  { value: "status", label: "Status" },
  { value: "assignee", label: "Assignee" },
  { value: "priority", label: "Priority" },
  { value: "issuetype", label: "Issue Type" },
  { value: "created", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "reporter", label: "Reporter" },
  { value: "severity", label: "Severity" },
  { value: "sprint", label: "Sprint" },
  { value: "storypoints", label: "Story Points" },
  { value: "labels", label: "Labels" },
  { value: "components", label: "Components" },
];

const INTERVALS = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

export default function JiraDatasetForm({ 
  datasourceId, 
  onSave, 
  onCancel,
  onDelete,
  initialData = null 
}) {
  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [jql, setJql] = useState("");
  
  // Aggregation state
  const [aggType, setAggType] = useState("count");
  const [groupBy, setGroupBy] = useState("");
  const [field, setField] = useState("");
  const [dateField, setDateField] = useState("created");
  const [interval, setIntervalVal] = useState("week");
  const [rowField, setRowField] = useState("status");
  const [columnField, setColumnField] = useState("priority");
  const [topN, setTopN] = useState(10);
  const [tableFields, setTableFields] = useState(["key", "summary", "status", "assignee"]);

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      // Load existing dataset data
      setName(initialData.name || "");
      setDescription(initialData.description || "");
      setJql(initialData.sql_query || "");
      
      // Load aggregation config
      const aggConfig = initialData.aggregation_config || {};
      setAggType(aggConfig.type || "count");
      setGroupBy(aggConfig.group_by || "");
      setField(aggConfig.field || "");
      setDateField(aggConfig.date_field || "created");
      setIntervalVal(aggConfig.interval || "week");
      setRowField(aggConfig.row_field || "status");
      setColumnField(aggConfig.column_field || "priority");
      setTopN(aggConfig.n || 10);
      setTableFields(aggConfig.fields || ["key", "summary", "status", "assignee"]);
      
      // Clear preview when loading existing
      setPreviewData(null);
      setPreviewError("");
    } else {
      // Reset to defaults for new dataset
      setName("");
      setDescription("");
      setJql("");
      setAggType("count");
      setGroupBy("");
      setField("");
      setDateField("created");
      setIntervalVal("week");
      setRowField("status");
      setColumnField("priority");
      setTopN(10);
      setTableFields(["key", "summary", "status", "assignee"]);
      setPreviewData(null);
      setPreviewError("");
    }
    setError("");
    setSuccessMessage("");
    setShowDeleteConfirm(false);
    setIsDeleted(false);
  }, [initialData]);

  // Get current aggregation type config
  const currentAggConfig = AGGREGATION_TYPES.find((t) => t.value === aggType);

  // Build aggregation config object
  const buildAggregationConfig = () => {
    const config = { type: aggType };

    if (currentAggConfig?.hasGroupBy && groupBy) {
      config.group_by = groupBy;
    }
    if (currentAggConfig?.hasField && field) {
      config.field = field;
    }
    if (currentAggConfig?.hasDateField) {
      config.date_field = dateField;
    }
    if (currentAggConfig?.hasInterval) {
      config.interval = interval;
    }
    if (currentAggConfig?.hasRowField) {
      config.row_field = rowField;
    }
    if (currentAggConfig?.hasColumnField) {
      config.column_field = columnField;
    }
    if (currentAggConfig?.hasN) {
      config.n = topN;
    }
    if (currentAggConfig?.hasFields) {
      config.fields = tableFields;
    }

    return config;
  };

  // Get output fields info for widget mapping
  const outputFieldsInfo = useMemo(() => {
    if (!currentAggConfig) return { fields: [], hint: "" };
    
    const config = buildAggregationConfig();
    const fields = currentAggConfig.outputFields?.(config) || [];
    const hint = currentAggConfig.widgetHint?.(config) || "";
    
    return { fields, hint };
  }, [currentAggConfig, aggType, groupBy, field, dateField, rowField, columnField, tableFields]);

  // Preview the query
  const handlePreview = async () => {
    if (!jql.trim()) {
      setPreviewError("Please enter a JQL query");
      return;
    }

    setPreviewLoading(true);
    setPreviewError("");
    setPreviewData(null);

    try {
      const aggConfig = buildAggregationConfig();
      const response = await apiPost("/query/jira/preview", {
        datasource_id: datasourceId,
        jql: jql,
        aggregation_config: aggConfig,
        limit: 100,
      });

      setPreviewData(response);
    } catch (err) {
      setPreviewError(err?.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Save the dataset
  const handleSave = async () => {
    if (!name.trim()) {
      setError("Please enter a dataset name");
      return;
    }
    if (!jql.trim()) {
      setError("Please enter a JQL query");
      return;
    }

    // Validate required fields
    if (currentAggConfig?.fieldRequired && !field) {
      setError(`Please select a field to ${aggType}`);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const aggConfig = buildAggregationConfig();
      
      const payload = {
        name: name.trim(),
        description: description.trim(),
        datasource_id: datasourceId,
        sql_query: jql.trim(),
        aggregation_config: aggConfig,
        is_jira: true,
      };

      console.log("[JiraDatasetForm] Saving with payload:", payload);

      if (initialData?.id) {
        // Update existing
        await apiPut(`/datasets/${initialData.id}`, payload);
      } else {
        // Create new
        await apiPost("/datasets/", payload);
      }
      
      onSave?.();
    } catch (err) {
      setError(err?.message || "Failed to save dataset");
    } finally {
      setIsLoading(false);
    }
  };

  // Delete the dataset
  const handleDelete = async () => {
    if (!initialData?.id) return;

    setIsDeleting(true);
    setError("");
    setSuccessMessage("");

    try {
      await apiDelete(`/datasets/${initialData.id}`);
      setIsDeleted(true);
      setShowDeleteConfirm(false);
      setSuccessMessage(`Dataset "${initialData.name}" deleted successfully!`);
      // Call onDelete callback after a short delay to show success message
      setTimeout(() => {
        onDelete?.(initialData.id);
      }, 1500);
    } catch (err) {
      setError(err?.message || "Failed to delete dataset");
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle table field toggle
  const toggleTableField = (fieldValue) => {
    setTableFields((prev) =>
      prev.includes(fieldValue)
        ? prev.filter((f) => f !== fieldValue)
        : [...prev, fieldValue]
    );
  };

  return (
    <div className="jira-dataset-form">
      <style>{`
        .jira-dataset-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .form-section {
          background: #f9fafb;
          border-radius: 8px;
          padding: 16px;
        }
        .form-section-title {
          font-weight: 600;
          margin-bottom: 12px;
          color: #374151;
        }
        .form-row {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }
        .form-group {
          flex: 1;
          min-width: 200px;
        }
        .form-group-full {
          flex: 100%;
        }
        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #4b5563;
          margin-bottom: 4px;
        }
        .form-input, .form-select, .form-textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          box-sizing: border-box;
        }
        .form-textarea {
          min-height: 80px;
          font-family: monospace;
        }
        .form-help {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 4px;
        }
        .agg-type-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 8px;
          margin-bottom: 16px;
        }
        .agg-type-btn {
          padding: 10px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          text-align: left;
          transition: all 0.2s;
        }
        .agg-type-btn:hover {
          border-color: #3b82f6;
        }
        .agg-type-btn.selected {
          border-color: #3b82f6;
          background: #eff6ff;
        }
        .agg-type-btn-title {
          font-weight: 600;
          font-size: 0.875rem;
          color: #1f2937;
        }
        .agg-type-btn-desc {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 2px;
        }
        .field-checkbox-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .field-checkbox {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .field-checkbox.checked {
          background: #eff6ff;
          border-color: #3b82f6;
        }
        .output-info {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          padding: 12px 16px;
          margin-top: 12px;
        }
        .output-info-title {
          font-weight: 600;
          font-size: 0.875rem;
          color: #1e40af;
          margin-bottom: 6px;
        }
        .output-fields {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          margin-bottom: 6px;
        }
        .output-field {
          background: white;
          border: 1px solid #93c5fd;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-family: monospace;
          color: #1e40af;
        }
        .output-hint {
          font-size: 0.75rem;
          color: #3b82f6;
        }
        .preview-section {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }
        .preview-title {
          font-weight: 600;
          color: #374151;
        }
        .preview-content {
          max-height: 300px;
          overflow: auto;
        }
        .preview-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .preview-table th, .preview-table td {
          padding: 8px 12px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        .preview-table th {
          background: #f9fafb;
          font-weight: 600;
        }
        .preview-empty {
          padding: 40px;
          text-align: center;
          color: #6b7280;
        }
        .preview-error {
          padding: 16px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 6px;
          margin: 16px;
        }
        .preview-loading {
          padding: 40px;
          text-align: center;
          color: #6b7280;
        }
        .preview-stats {
          padding: 8px 16px;
          background: #f0fdf4;
          color: #166534;
          font-size: 0.875rem;
        }
        .btn-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .btn-row-left {
          display: flex;
          gap: 12px;
        }
        .btn-row-right {
          display: flex;
          gap: 12px;
        }
        .btn {
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          font-size: 0.875rem;
        }
        .btn-primary {
          background: #3b82f6;
          color: white;
        }
        .btn-primary:hover {
          background: #2563eb;
        }
        .btn-primary:disabled {
          background: #93c5fd;
          cursor: not-allowed;
        }
        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        }
        .btn-secondary:hover {
          background: #e5e7eb;
        }
        .btn-danger {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fca5a5;
        }
        .btn-danger:hover {
          background: #fecaca;
        }
        .btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .error-message {
          padding: 12px 16px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 6px;
          margin-bottom: 16px;
        }
        .success-message {
          padding: 12px 16px;
          background: #f0fdf4;
          color: #166534;
          border-radius: 6px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .deleted-state {
          padding: 40px;
          text-align: center;
          background: #f0fdf4;
          border-radius: 8px;
          margin: 20px 0;
        }
        .deleted-state-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }
        .deleted-state-text {
          font-size: 1.1rem;
          color: #166534;
          font-weight: 500;
        }
        .delete-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .delete-modal {
          background: white;
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
        .delete-modal-icon {
          font-size: 48px;
          text-align: center;
          margin-bottom: 16px;
        }
        .delete-modal-title {
          font-weight: 600;
          font-size: 1.25rem;
          color: #dc2626;
          text-align: center;
          margin-bottom: 8px;
        }
        .delete-modal-text {
          font-size: 0.95rem;
          color: #4b5563;
          text-align: center;
          margin-bottom: 20px;
        }
        .delete-modal-name {
          font-weight: 600;
          color: #1f2937;
        }
        .delete-modal-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
        }
        .current-config {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 16px;
        }
        .current-config-title {
          font-weight: 600;
          font-size: 0.875rem;
          color: #92400e;
          margin-bottom: 4px;
        }
        .current-config-content {
          font-size: 0.8rem;
          color: #78350f;
          font-family: monospace;
        }
        .optgroup-label {
          font-weight: 600;
          color: #374151;
          background: #f3f4f6;
        }
      `}</style>

      {error && <div className="error-message">❌ {error}</div>}
      {successMessage && <div className="success-message">✅ {successMessage}</div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">⚠️</div>
            <div className="delete-modal-title">Delete Dataset?</div>
            <div className="delete-modal-text">
              Are you sure you want to delete <span className="delete-modal-name">"{initialData?.name}"</span>?
              <br />This action cannot be undone.
            </div>
            <div className="delete-modal-buttons">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show deleted state */}
      {isDeleted ? (
        <div className="deleted-state">
          <div className="deleted-state-icon">✅</div>
          <div className="deleted-state-text">Dataset deleted successfully!</div>
        </div>
      ) : (
        <>
        {/* Show current loaded config for editing */}
      {initialData?.id && (
        <div className="current-config">
          <div className="current-config-title">📝 Editing Dataset: {initialData.name}</div>
          <div className="current-config-content">
            JQL: {jql || "(not set)"} | Aggregation: {aggType}
            {groupBy && ` | Group By: ${groupBy}`}
            {field && ` | Field: ${field}`}
          </div>
        </div>
      )}

      {/* Basic Info */}
      <div className="form-section">
        <div className="form-section-title">📋 Dataset Information</div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Dataset Name *</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., XQT Bug Count by Status"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input
              type="text"
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>
        </div>
      </div>

      {/* JQL Query */}
      <div className="form-section">
        <div className="form-section-title">🔍 JQL Query (Filter)</div>
        <div className="form-group form-group-full">
          <label className="form-label">JQL Query *</label>
          <textarea
            className="form-textarea"
            value={jql}
            onChange={(e) => setJql(e.target.value)}
            placeholder="e.g., project = XQT AND issuetype = Bug AND status != Done"
          />
          <div className="form-help">
            Enter the JQL to filter issues. This works like JIRA's search filter.
            <br />
            Examples: <code>project = XQT</code>, <code>issuetype = Bug AND priority = High</code>
          </div>
        </div>
      </div>

      {/* Aggregation Type */}
      <div className="form-section">
        <div className="form-section-title">📊 Aggregation (How to Process Results)</div>
        <div className="form-help" style={{ marginBottom: "12px" }}>
          JQL only filters issues - it cannot aggregate (COUNT, SUM, GROUP BY). 
          Select how you want to process the filtered results:
        </div>
        
        <div className="agg-type-grid">
          {AGGREGATION_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`agg-type-btn ${aggType === type.value ? "selected" : ""}`}
              onClick={() => setAggType(type.value)}
            >
              <div className="agg-type-btn-title">{type.label}</div>
              <div className="agg-type-btn-desc">{type.description}</div>
            </button>
          ))}
        </div>

        {/* Aggregation Parameters */}
        <div className="form-row">
          {currentAggConfig?.hasGroupBy && (
            <div className="form-group">
              <label className="form-label">Group By</label>
              <select
                className="form-select"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <option value="">No grouping (total only)</option>
                <optgroup label="Categorical Fields">
                  <option value="status">Status</option>
                  <option value="assignee">Assignee</option>
                  <option value="reporter">Reporter</option>
                  <option value="priority">Priority</option>
                  <option value="issuetype">Issue Type</option>
                  <option value="project">Project</option>
                  <option value="resolution">Resolution</option>
                  <option value="sprint">Sprint</option>
                  <option value="labels">Labels</option>
                  <option value="components">Components</option>
                  <option value="fixVersions">Fix Versions</option>
                  <option value="severity">Severity</option>
                </optgroup>
                <optgroup label="Date Fields (for trends)">
                  <option value="created">Created Date</option>
                  <option value="updated">Updated Date</option>
                  <option value="resolutiondate">Resolution Date</option>
                  <option value="duedate">Due Date</option>
                </optgroup>
                <optgroup label="Calculated Fields (Aging Analysis)">
                  <option value="age_bucket">Age Bucket (0-7, 8-14, 15-30, 31-60, 61-90, 90+ days)</option>
                </optgroup>
              </select>
              <div className="form-help">
                💡 For trend charts, use "Time Series". For defect aging, use "Age Bucket".
              </div>
            </div>
          )}

          {currentAggConfig?.hasField && (
            <div className="form-group">
              <label className="form-label">
                {currentAggConfig.fieldLabel || "Field"} 
                {currentAggConfig.fieldRequired && " *"}
              </label>
              <select
                className="form-select"
                value={field}
                onChange={(e) => setField(e.target.value)}
              >
                <option value="">{currentAggConfig.fieldRequired ? "Select field" : "(Count issues)"}</option>
                {NUMERIC_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          )}

          {currentAggConfig?.hasDateField && (
            <div className="form-group">
              <label className="form-label">Date Field</label>
              <select
                className="form-select"
                value={dateField}
                onChange={(e) => setDateField(e.target.value)}
              >
                {DATE_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          )}

          {currentAggConfig?.hasInterval && (
            <div className="form-group">
              <label className="form-label">Interval</label>
              <select
                className="form-select"
                value={interval}
                onChange={(e) => setIntervalVal(e.target.value)}
              >
                {INTERVALS.map((i) => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </div>
          )}

          {currentAggConfig?.hasRowField && (
            <div className="form-group">
              <label className="form-label">Row Field</label>
              <select
                className="form-select"
                value={rowField}
                onChange={(e) => setRowField(e.target.value)}
              >
                {COMMON_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          )}

          {currentAggConfig?.hasColumnField && (
            <div className="form-group">
              <label className="form-label">Column Field</label>
              <select
                className="form-select"
                value={columnField}
                onChange={(e) => setColumnField(e.target.value)}
              >
                {COMMON_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          )}

          {currentAggConfig?.hasN && (
            <div className="form-group">
              <label className="form-label">Top N</label>
              <input
                type="number"
                className="form-input"
                value={topN}
                onChange={(e) => setTopN(parseInt(e.target.value) || 10)}
                min={1}
                max={100}
              />
            </div>
          )}
        </div>

        {/* Table Fields Selection */}
        {currentAggConfig?.hasFields && (
          <div className="form-group">
            <label className="form-label">Select Fields to Include</label>
            <div className="field-checkbox-grid">
              {TABLE_FIELDS.map((f) => (
                <label
                  key={f.value}
                  className={`field-checkbox ${tableFields.includes(f.value) ? "checked" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={tableFields.includes(f.value)}
                    onChange={() => toggleTableField(f.value)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Output Fields Info */}
        <div className="output-info">
          <div className="output-info-title">📤 Output Fields (for Widget Mapping)</div>
          <div className="output-fields">
            {outputFieldsInfo.fields.map((f, idx) => (
              <span key={idx} className="output-field">{f}</span>
            ))}
          </div>
          <div className="output-hint">
            💡 Widget hint: {outputFieldsInfo.hint}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="preview-section">
        <div className="preview-header">
          <span className="preview-title">📊 Preview</span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePreview}
            disabled={previewLoading || !jql.trim()}
          >
            {previewLoading ? "Loading..." : "Run Preview"}
          </button>
        </div>

        {previewError && (
          <div className="preview-error">{previewError}</div>
        )}

        {previewLoading && (
          <div className="preview-loading">
            <div>⏳ Fetching data from JIRA...</div>
          </div>
        )}

        {previewData && !previewLoading && (
          <>
            <div className="preview-stats">
              ✅ Found {previewData.total_issues} issues → Aggregated to {previewData.total} rows
            </div>
            <div className="preview-content">
              {previewData.data?.length > 0 ? (
                <table className="preview-table">
                  <thead>
                    <tr>
                      {Object.keys(previewData.data[0]).map((key) => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.data.slice(0, 20).map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((val, i) => (
                          <td key={i}>{String(val ?? "")}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="preview-empty">No data returned</div>
              )}
              {previewData.data?.length > 20 && (
                <div className="preview-stats">
                  Showing first 20 of {previewData.data.length} rows
                </div>
              )}
            </div>
          </>
        )}

        {!previewData && !previewLoading && !previewError && (
          <div className="preview-empty">
            Click "Run Preview" to test your JQL and see aggregated results
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="btn-row">
        <div className="btn-row-left">
          {initialData?.id && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading || isDeleting}
            >
              🗑️ Delete
            </button>
          )}
        </div>
        <div className="btn-row-right">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isLoading || !name.trim() || !jql.trim()}
          >
            {isLoading ? "Saving..." : initialData?.id ? "Update Dataset" : "Save Dataset"}
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
}